import * as Sentry from "@sentry/react";

/**
 * Single logging entry point for the app (Atlas: Logger & Error Handling).
 * Never call `console.*` directly in application code — route everything through `logger`.
 *
 *   logger.log(...)   / logger.debug(...)  — dev-only, dead-code-eliminated in prod builds
 *   logger.warn(...)                       — always logs (security/auth warnings, invalid input)
 *   logger.error(error)                    — console + Sentry (prod)
 *   logger.error("message", error)         — descriptive prefix + Sentry
 *
 * This baseline has no Redux error-log slice or snackbar coupling yet (those land with the
 * feature vertical slices); the surface here matches the discipline's documented API so callers
 * never need to change when that machinery arrives.
 */

const isDev = import.meta.env.DEV;

const logger = {
	/** Dev-only — stripped from production builds. */
	log: (...args: unknown[]) => {
		if (isDev) console.log(...args);
	},
	/** Dev-only — stripped from production builds. */
	debug: (...args: unknown[]) => {
		if (isDev) console.debug(...args);
	},
	/** Always logs to the console — used for prod-visible security/auth warnings. */
	warn: (...args: unknown[]) => {
		console.warn(...args);
	},
	/**
	 * Always logs to the console and captures to Sentry in production.
	 * Signatures: logger.error(error) | logger.error("message", error).
	 */
	error: (errorOrMessage: unknown, maybeError?: unknown) => {
		let message: string | undefined;
		let error: unknown;

		if (typeof errorOrMessage === "string") {
			message = errorOrMessage;
			error = maybeError;
		} else {
			error = errorOrMessage;
		}

		if (message) {
			console.error(message, error);
		} else {
			console.error(error);
		}

		if (!isDev) {
			Sentry.withScope((scope) => {
				scope.setTag("error.source", "logger");
				if (message) scope.setExtra("message", message);
				if (error !== undefined) {
					Sentry.captureException(error);
				} else if (message) {
					Sentry.captureMessage(message, "error");
				}
			});
		}
	}
};

export default logger;
